<template>
  <div :id="'component-' + gradingRubricUuid" class="grading-rubric-view">
    <cv-inline-notification
      v-if="hasError"
      class="rubric-notification"
      :class="errorClass"
      :title="errorMessage"
      low-contrast
      kind="error"
      @close="handleCloseError"
    />
    <AccordionWrapper @state-change="handleChange">
      <cv-accordion-item :open="isAccordionOpen">
        <template v-if="gradingRubric.title" #title>{{ gradingRubric.title }}</template>
        <template v-else #title>{{ t("rubric_header") }}</template>
        <template #content>
          <div v-if="gradingRubric.hideRubrics" class="criteria-info">
            <span v-if="isQuizGradingRubric">
              {{ t("grading_rubric_hide_rubrics_before_attempt_indicator_quiz") }}
            </span>
            <span v-else>
              {{ t("grading_rubric_hide_rubrics_before_attempt_indicator_question") }}
            </span>
          </div>
          <grading-rubric-criterion-view
            v-for="(criteria, index) in processedCriteriaList"
            :key="criteria.id"
            :grading-rubric-uuid="gradingRubricUuid"
            :criteria-label="showLabel(index)"
            :criteria-content="criteria"
            :show-mark-range="showMarkRange"
            :visible="visible"
            :expanded="isExpanded"
            :is-quiz-grading-rubric="isQuizGradingRubric"
          />
        </template>
      </cv-accordion-item>
    </AccordionWrapper>
  </div>
</template>

<script setup lang="ts">
import { isDefined, isEmpty } from "@utils/var.check";
import { ALPHABETS_LIST } from "@modules/editor/service/editor.constant";
import GradingRubricCriterionView from "./GradingRubricCriterionView.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useLessonSubmissionValidation } from "@modules/editor/composables/lesson-submission-validation.ts";
import { useGradingRubric } from "@modules/editor/composables/grading-rubric.ts";

const { t } = useI18n();

const { checkIfUnapprovedMOValidationErrorsExist } = useLessonSubmissionValidation();

// Data
const isExpanded = ref(false);
const hasError = ref(false);
const errorMessage = ref("");
const errorClass = ref("");

// Props
const props = defineProps({
  question: { type: Object, required: true },
  visible: { type: Boolean, default: true },
  showMarkRange: { type: Boolean, default: false },
  gradingRubric: { type: Object, default: null },
  isFromSearchResourcePreview: { type: Boolean, default: false },
  isQuizGradingRubric: { type: Boolean, default: false },
  quiz: { type: Object, default: () => ({}) },
  sortedQuizQuestionData: { type: Array, default: () => [] },
});

const { isAccordionOpen, isParentEdited } = useGradingRubric(
  {
    ...props,
    isNewQuestion: false,
    mode: "",
  },
  {
    isProcessing: ref(false),
    getProcessingTriggerElement: ref(undefined),
  },
);

// Computed
const processedCriteriaList = computed(() => {
  return populateCriteriaRangeData();
});

const gradingRubricUuid = computed(() => {
  return props.gradingRubric.uuid;
});

const quizQuestionIdMap = computed(() => {
  return new Map(props.sortedQuizQuestionData.map((question) => [question.id, question]));
});

// Methods
const handleCloseError = () => {
  hasError.value = false;
  errorMessage.value = "";
};

const checkIfSubmitValidationErrorsExist = () => {
  hasError.value = false;
  if (checkIfUnapprovedMOValidationErrorsExist(props.gradingRubric.uuid)) {
    hasError.value = true;
    errorClass.value = "submit-validation-error";
    errorMessage.value = t("grading_rubric_validation_error");
  }
};

const handleChange = (event) => {
  if (event.state[0]) {
    isExpanded.value = true;
  }
};

const showLabel = (index) => {
  return ALPHABETS_LIST[index];
};

const populateCriteriaRangeData = () => {
  // Deep cloning here as Object.assign will do shallow clone
  const cloneRubric = isDefined(props.gradingRubric)
    ? JSON.parse(JSON.stringify(props.gradingRubric))
    : null;
  if (!cloneRubric?.criteriaModelList?.length) {
    return [];
  }
  // API may return criteriaRangeModelList and rangeModelList nested per-criteria.
  // Flatten them to the top-level structure the downstream functions expect.
  if (!cloneRubric.criteriaRangeModelList?.length) {
    cloneRubric.criteriaRangeModelList = cloneRubric.criteriaModelList.flatMap(
      (criteria) => criteria.criteriaRangeModelList ?? [],
    );
  }
  if (!cloneRubric.rangeModelList?.length) {
    cloneRubric.rangeModelList = cloneRubric.criteriaModelList.flatMap(
      (criteria) => criteria.rangeModelList ?? [],
    );
  }
  if (!cloneRubric.criteriaRangeModelList.length) {
    return [];
  }
  populateMinimumScore(cloneRubric);
  populateRangeDescription(cloneRubric);
  return generateCriteriaRowData(cloneRubric);
};

const populateMinimumScore = (rubric) => {
  let previousCellMaxScore = 0;
  let currentRow = 0;
  const criteriaRangeList = rubric.criteriaRangeModelList;
  criteriaRangeList.forEach((criteriaRange) => {
    // iterate on next criteria and reset max score
    if (currentRow !== criteriaRange.criteriaIndex) {
      previousCellMaxScore = 0;
      currentRow = criteriaRange.criteriaIndex;
    }
    if (criteriaRange.rangeIndex === 0) {
      criteriaRange.minScore = 0;
    } else {
      criteriaRange.minScore = previousCellMaxScore + 1;
    }
    previousCellMaxScore = criteriaRange.maxScore;
  });
};

const populateRangeDescription = (rubric) => {
  const criteriaList = rubric.criteriaModelList;
  const rangeList = rubric.rangeModelList;
  const criteriaRangeList = rubric.criteriaRangeModelList;

  criteriaRangeList.forEach((criteriaRange) => {
    const criteriaForRange = criteriaList.find((criteria) => {
      return criteria.indexNo === criteriaRange.criteriaIndex;
    });

    const criteriaRangeListByCriterion = criteriaForRange?.rangeModelList ?? [];

    const matchedRangeInCriterion = criteriaRangeListByCriterion.find((range) => {
      return (
        range.id === criteriaRange.gradingRubricRangeId ||
        range.indexNo === criteriaRange.rangeIndex
      );
    });

    if (matchedRangeInCriterion) {
      criteriaRange.headerDescription = matchedRangeInCriterion.description;
      return;
    }

    // Fallback for payloads that only include top-level flattened rangeModelList.
    const matchedRangeInFlatList = rangeList.find((range) => {
      const isMatchedRangeId = range.id === criteriaRange.gradingRubricRangeId;
      const isMatchedRangeIndex = range.indexNo === criteriaRange.rangeIndex;
      return isMatchedRangeId || isMatchedRangeIndex;
    });

    criteriaRange.headerDescription = matchedRangeInFlatList?.description ?? "";
  });
};

const generateCriteriaRowData = (rubric) => {
  // process each cell to be populated into relevant criteria(row)
  // sample result after process:
  // criteriaRow = {
  //   "0": [ {"0": criteriaIndex: 0, rangeIndex: 0, maxScore: 2, minScore: 0},
  //          {"1": criteriaIndex: 0, rangeIndex: 1, maxScore: 4, minScore: 3}]
  //   "1": [ {"1": criteriaIndex: 0, rangeIndex: 0, maxScore: 2, minScore: 0}]
  // };
  const criteriaList = rubric.criteriaModelList;
  const criteriaRangeList = rubric.criteriaRangeModelList;
  const criteriaRow = criteriaRangeList.reduce((row, criteriaRange) => {
    row[criteriaRange.criteriaIndex] = [...(row[criteriaRange.criteriaIndex] || []), criteriaRange];
    return row;
  }, []);

  criteriaRow.forEach((row, criteriaIndex) => {
    const matchedCriteria = criteriaList.find((criteria) => {
      return criteriaIndex === criteria.indexNo;
    });

    let maxScore = 0;
    if (props.isQuizGradingRubric) {
      maxScore = populateQuizGradingRubricCriteriaMaxScore(matchedCriteria);
    } else {
      row.forEach((mark) => {
        if (maxScore < mark.maxScore) {
          maxScore = mark.maxScore;
        }
      });
    }

    row["criteriaRangesData"] = [...row];
    row.splice(0, row.length); // remove unused data
    row.criteriaDescription = matchedCriteria.description;
    row.criteriaMarks = maxScore;

    if (props.isQuizGradingRubric) {
      row.selectedQuestionTags = populateSelectedQuestionTags(matchedCriteria);
    }
  });
  return criteriaRow;
};

const populateQuizGradingRubricCriteriaMaxScore = (criteria) => {
  let maxScore = 0;
  if (isEmpty(criteria.questionIds)) {
    return maxScore;
  }

  criteria.questionIds.forEach((questionId) => {
    const question = quizQuestionIdMap.value.get(questionId);

    if (!isEmpty(question)) {
      maxScore += question.marks;
    }
  });

  return maxScore;
};

const populateSelectedQuestionTags = (criteria) => {
  if (isEmpty(criteria.questionIds)) {
    return [];
  }

  const tags = [];
  const questionIdSet = new Set(criteria.questionIds);

  props.sortedQuizQuestionData.forEach((question) => {
    if (questionIdSet.has(question.id)) {
      tags.push({
        label: question.questionLabel,
      });
    }
  });

  return tags;
};

// Created
if (!props.isQuizGradingRubric && !props.isFromSearchResourcePreview) {
  isExpanded.value = isParentEdited.value;
}

// Mounted
onMounted(() => {
  checkIfSubmitValidationErrorsExist();
});
</script>
